import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type SubmitPicksInput } from '@/services/api';
import { poolKeys } from './usePools';

/**
 * Query key factory for picks
 */
export const pickKeys = {
  all: ['picks'] as const,
  lists: () => [...pickKeys.all, 'list'] as const,
  myPicks: (poolId: string) => [...pickKeys.lists(), poolId, 'mine'] as const,
  allPicks: (poolId: string) => [...pickKeys.lists(), poolId, 'all'] as const,
};

/**
 * Hook to get the current user's picks for a pool
 */
export function useMyPicks(poolId: string) {
  return useQuery({
    queryKey: pickKeys.myPicks(poolId),
    queryFn: async () => {
      const response = await api.picks.getMyPicks(poolId);
      if (response.error) {
        throw new Error(response.error.message);
      }
      return response.data;
    },
    enabled: !!poolId,
  });
}

/**
 * Hook to get all picks for a pool (only available after deadline)
 */
export function useAllPicks(poolId: string, enabled = true) {
  return useQuery({
    queryKey: pickKeys.allPicks(poolId),
    queryFn: async () => {
      const response = await api.picks.getAllPicks(poolId);
      if (response.error) {
        throw new Error(response.error.message);
      }
      return response.data;
    },
    enabled: !!poolId && enabled,
  });
}

/**
 * Hook to submit picks for a pool
 */
export function useSubmitPicks(poolId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: SubmitPicksInput) => {
      const response = await api.picks.submitPicks(poolId, input);
      if (response.error) {
        throw new Error(response.error.message);
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pickKeys.myPicks(poolId) });
      queryClient.invalidateQueries({ queryKey: poolKeys.detail(poolId) });
    },
  });
}
